import axios from 'axios';
import { isFunction, isArray, isString, isUndefined } from 'lodash-es';
import { onUnmounted, onDeactivated } from 'vue';
import { MD5 } from 'crypto-js';

const ComposeFunc = (direction, defaultAction, plugins) => ({
  add(...newPlugins) {
    if (!newPlugins.length)
      return this;
    return ComposeFunc(direction, defaultAction, [...plugins, ...newPlugins]);
  },
  exec(options) {
    const method = direction === 0 /* LEFT_TO_RIGHT */ ? plugins.reduceRight : plugins.reduce;
    let opts = options;
    const getOpts = (config) => {
      if (config)
        opts = config;
      return opts;
    };
    const func = method.call(
      plugins,
      (acc, x) => (config) => x(acc, getOpts(config)),
      (config) => defaultAction(getOpts(config))
    );
    return func(options);
  }
});
const compose = (defaultAction, ...plugins) => ComposeFunc(0 /* LEFT_TO_RIGHT */, defaultAction, plugins);

const getOrder = (plugin) => isFunction(plugin) ? 0 : plugin.order;
const fetch = (defaultAction, plugins) => {
  let cachedExecutor;
  return {
    add(list) {
      if (!list.length)
        return this;
      return fetch(defaultAction, plugins.concat(list));
    },
    attach(callback) {
      const newPlugins = new Promise((resolve, reject) => {
        try {
          resolve(callback(plugins));
        } catch (e) {
          reject(e);
        }
      });
      return newPlugins.then((list) => fetch(defaultAction, list));
    },
    exec(args) {
      if (!cachedExecutor) {
        const list = plugins.concat([]);
        list.sort((x, y) => getOrder(x) - getOrder(y));
        const data = list.map((v) => isFunction(v) ? v : v.executor);
        cachedExecutor = compose(defaultAction, ...data).exec;
      }
      return new Promise((resolve, reject) => {
        try {
          resolve(cachedExecutor(args));
        } catch (e) {
          reject(e);
        }
      });
    }
  };
};
const ajax = (plugins) => fetch((config) => axios(config).then((v) => ({ ...v, config })), plugins);

var AjaxMethods = /* @__PURE__ */ ((AjaxMethods2) => {
  AjaxMethods2["GET"] = "GET";
  AjaxMethods2["DELETE"] = "DELETE";
  AjaxMethods2["HEAD"] = "HEAD";
  AjaxMethods2["OPTIONS"] = "OPTIONS";
  AjaxMethods2["POST"] = "POST";
  AjaxMethods2["PUT"] = "PUT";
  AjaxMethods2["PATCH"] = "PATCH";
  return AjaxMethods2;
})(AjaxMethods || {});

class AjaxContainer {
  constructor(plugins = []) {
    this.$agent = ajax(plugins);
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.send = this.send.bind(this);
    this.attach = this.attach.bind(this);
    this.upload = this.upload.bind(this);
    this.download = this.download.bind(this);
  }
  send(option = {}, plugins) {
    const { data, body, ...reset } = option;
    return this.$agent.attach((list) => plugins.length ? list.concat(plugins) : list).then((v) => v.exec({ ...reset, data: body || data }));
  }
  get(url, option = {}, ...plugins) {
    return this.send({ ...option, method: AjaxMethods.GET, url }, plugins);
  }
  put(url, option = {}, ...plugins) {
    return this.send({ ...option, method: AjaxMethods.PUT, url }, plugins);
  }
  post(url, option = {}, ...plugins) {
    return this.send({ ...option, method: AjaxMethods.POST, url }, plugins);
  }
  upload(url, data = {}, meta = {}) {
    return this.post(url, {
      data,
      meta: { upload: true, aes: false, ...meta }
    });
  }
  download(url, data = {}, meta = {}) {
    return this.post(url, {
      responseType: "blob",
      data,
      meta: { isTransformRequestResult: false, ...meta }
    });
  }
  attach(callback) {
    if (isArray(callback)) {
      if (callback.length) {
        return this.$agent.attach((list) => list.concat(callback)).then((v) => this.$agent = v);
      }
      return Promise.resolve(this.$agent);
    } else {
      return this.$agent.attach(callback).then((v) => this.$agent = v);
    }
  }
  getAgent() {
    return this.$agent;
  }
}

var ContentTypeEnum = /* @__PURE__ */ ((ContentTypeEnum2) => {
  ContentTypeEnum2["JSON"] = "application/json;charset=UTF-8";
  ContentTypeEnum2["FORM_URLENCODED"] = "application/x-www-form-urlencoded;charset=UTF-8";
  ContentTypeEnum2["FORM_DATA"] = "multipart/form-data;charset=UTF-8";
  ContentTypeEnum2["STREAM"] = "application/octet-stream";
  ContentTypeEnum2["XPROTO"] = "application/x-protobuf";
  return ContentTypeEnum2;
})(ContentTypeEnum || {});
const errorResult = "__ERROR_RESULT__";
const errorResultNull = "__ERROR_RESULT_NULL__";

let pendingMap = /* @__PURE__ */ new Map();
const getPendingUrl = (config) => [config.url, config.timestamp].join("&");
class AxiosCanceler {
  addPending(config) {
    this.removePending(config);
    const url = getPendingUrl(config);
    config.cancelToken = config.cancelToken || new axios.CancelToken((cancel) => {
      if (!pendingMap.has(url)) {
        pendingMap.set(url, cancel);
      }
    });
  }
  removeAllPending() {
    pendingMap.forEach((cancel) => {
      cancel && isFunction(cancel) && cancel("Cancel:");
    });
    pendingMap.clear();
  }
  removePending(config) {
    const url = getPendingUrl(config);
    if (pendingMap.has(url)) {
      const cancel = pendingMap.get(url);
      cancel && cancel(`Cancel:${url}`);
      pendingMap.delete(url);
    }
  }
  reset() {
    pendingMap = /* @__PURE__ */ new Map();
  }
}

const axiosCanceler = new AxiosCanceler();
const cancelAllAjax = () => axiosCanceler.removeAllPending();
const addCancelerPlugin = (next, config) => {
  config.timestamp = `${Date.now()}-${~~(Math.random() * 1e3)}`;
  const { headers: { ignoreCancelToken } = { ignoreCancelToken: false } } = config;
  !ignoreCancelToken && axiosCanceler.addPending(config);
  if (config.meta)
    config.meta.canceler = () => axiosCanceler.removePending(config);
  else
    config.meta = { canceler: () => axiosCanceler.removePending(config) };
  return next();
};
const removeCancelerPlugin = async (next, config) => {
  const delay = new Promise((resolve, reject) => {
    try {
      resolve(next());
    } catch (e) {
      reject(e);
    }
  });
  if (config.meta && config.meta.canceler) {
    const remove = config.meta.canceler;
    delay.then(remove, remove);
  }
  return delay.catch((error) => {
    var _a;
    if ((_a = error == null ? void 0 : error.message) == null ? void 0 : _a.startsWith("Cancel"))
      return Promise.reject({ cancel: true, ...error });
    return Promise.reject(error);
  });
};

const handleSuccess = (res, meta, options) => {
  const { isTransformRequestResult = true, allReturn } = meta;
  const resultKey = (options == null ? void 0 : options.resultKey) || "data";
  if (allReturn)
    return res;
  if (!isTransformRequestResult)
    return res[resultKey] || res;
  const result = res[resultKey];
  if (!result) {
    return Promise.reject(errorResultNull);
  }
  const { code, data, message } = result;
  if (code === ((options == null ? void 0 : options.successCode) || 200))
    return data;
  options == null ? void 0 : options.checkErrorCode(code, message, result, meta);
  return Promise.reject(new Error(result));
};
const handleError = (error, options, meta) => {
  const { code, message } = error || {};
  const err = error.toString();
  if (code === "ECONNABORTED" && message.includes("timeout"))
    options == null ? void 0 : options.checkHttpErrorCode(":timeout", meta);
  if (err && err.includes("Network Error"))
    options == null ? void 0 : options.checkHttpErrorCode(":networkError", meta);
  (options == null ? void 0 : options.checkHttpErrorCode) && options.checkHttpErrorCode(error, meta);
  return Promise.reject(error);
};
const buildAfterPlugin = (next, config) => {
  const { afterPluginOption } = getNiceAxiosOptions() || {};
  const delay = new Promise((resolve, reject) => {
    try {
      resolve(next());
    } catch (e) {
      console.error({ req: config.data, url: config.url });
      reject(e);
    }
  });
  return delay.then(
    (v) => handleSuccess(v, config.meta || {}, afterPluginOption),
    (e) => handleError(e, afterPluginOption, config.meta)
  );
};

function stringifyParams(obj) {
  let parameters = "";
  for (const key in obj)
    parameters += `${key}=${encodeURIComponent(obj[key])}&`;
  parameters = parameters.replace(/&$/, "");
  return parameters;
}
const sleep = (time) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve("");
    }, time);
  });
};

function getMultipartConfig(config) {
  const { data } = config;
  const formData = new FormData();
  if (isString(data))
    throw new Error("getMultipartConfig: multi part can not be string.");
  if (!(data instanceof FormData) && data) {
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(`${key}[]`, item);
        });
        return;
      }
      formData.append(key, data[key]);
    });
  }
  if (config.headers) {
    config.headers["Content-type"] = ContentTypeEnum.FORM_DATA;
    config.headers.ignoreCancelToken = true;
  } else {
    config.headers = { "Content-type": ContentTypeEnum.FORM_DATA, "ignoreCancelToken": true };
  }
  config.method = AjaxMethods.POST;
  config.data = formData;
  return config;
}
const isHttpUrl = (url) => {
  if (!url)
    return false;
  return url.startsWith("http");
};

const buildBeforePlugin = (next, originalConfig) => {
  let config = originalConfig;
  if (!config.meta)
    config.meta = { showErrorTip: true };
  const { meta } = config;
  if (isUndefined(config.meta.showErrorTip))
    config.meta.showErrorTip = true;
  const { joinPrefix } = meta;
  const { prefixURL, baseURL = "/" } = getNiceAxiosOptions() || {};
  if (!config.baseURL)
    config.baseURL = baseURL;
  if (config.url && !isHttpUrl(config.url)) {
    if (joinPrefix && prefixURL)
      config.url = `${prefixURL}${config.url}`;
  }
  if (!config.headers)
    config.headers = {};
  if (config.method === AjaxMethods.GET) {
    if (!isString(config.params)) {
      config.data = {
        params: { ...config.params, _t: Date.now() }
      };
    }
  } else {
    if (!config.method) {
      config.method = AjaxMethods.POST;
      config.withCredentials = true;
    }
    if (meta == null ? void 0 : meta.form) {
      config.headers["Content-Type"] = ContentTypeEnum.FORM_URLENCODED;
      if (config.data instanceof FormData || isString(config.data))
        throw new Error("buildBeforePlugin: config.data must be complex object on config.meta.form === true.");
      else
        config.data = config.data ? stringifyParams(config.data) : "";
      config.params = void 0;
    } else if (meta == null ? void 0 : meta.upload) {
      config = getMultipartConfig(config);
    }
  }
  return next();
};

const requestPromise = /* @__PURE__ */ new Map();
const mergeRequestPlugin = async (next, config) => {
  var _a;
  if (((_a = config.meta) == null ? void 0 : _a.merge) === false)
    return next();
  const requestId = MD5(JSON.stringify(config));
  const exitPromise = requestPromise.get(requestId.toString());
  if (!exitPromise) {
    const promise = next();
    requestPromise.set(requestId.toString(), promise);
    return promise.finally((result) => {
      sleep(200).then(() => {
        requestPromise.delete(requestId.toString());
      });
      return result;
    });
  } else {
    return exitPromise;
  }
};

let instance;
let niceAxiosOptions;
const plugins = [
  { desc: "\u6DFB\u52A0\u5F53\u524D Ajax \u8BF7\u6C42\u7684\u624B\u52A8\u53D6\u6D88\u529F\u80FD\uFF1A\u5E94\u8BE5\u5728\u6240\u6709\u63D2\u4EF6\u4E2D\u7684\u7B2C\u4E00\u4E2A", order: -1040, executor: addCancelerPlugin },
  { desc: "\u5408\u5E76\u8BF7\u6C42", order: -1020, executor: mergeRequestPlugin },
  {
    desc: "\u57FA\u7840\u4E1A\u52A1\u524D\u7F6E\u63D2\u4EF6",
    order: -1e3,
    executor: buildBeforePlugin
  },
  { desc: "\u901A\u7528\u540E\u7F6E\u903B\u8F91", order: 1e3, executor: buildAfterPlugin },
  { desc: "\u79FB\u9664\u5F53\u524D\u8BF7\u6C42\u7684\u624B\u52A8\u53D6\u6D88\u529F\u80FD\uFF1A\u5E94\u8BE5\u5728\u6240\u6709\u63D2\u4EF6\u4E2D\u7684\u6700\u540E\u4E00\u4E2A", order: 0, executor: removeCancelerPlugin }
];
const createNiceAxios = (customPlugins = [], options) => {
  if (instance) {
    console.warn("the nice_axios instance is created");
    return;
  }
  niceAxiosOptions = options;
  instance = new AjaxContainer([...plugins, ...customPlugins]);
  return instance;
};
const getNiceAxiosInstance = () => {
  if (!instance)
    throw new Error("the nice_axios instance is not created, please create first.");
  return instance;
};
const getNiceAxiosOptions = () => {
  return niceAxiosOptions;
};

class AjaxHookInstance {
  constructor(agent, config) {
    this.agent = agent;
    this.config = typeof config === "string" ? { url: config } : config;
  }
  add(...plugins) {
    const newAgent = this.agent.then((v) => v.add(plugins));
    return new AjaxHookInstance(newAgent, this.config);
  }
  exec(config) {
    const currentConfig = !config ? {} : config;
    this.config.timestamp = Date.now();
    return this.agent.then((v) => {
      const parameter = {
        ...this.config,
        ...currentConfig,
        params: { ...this.config.params, ...currentConfig.params },
        data: currentConfig.data || {}
      };
      return v.exec(parameter);
    });
  }
  send(data = {}) {
    return this.exec(data);
  }
  sendQuery(query) {
    return this.exec({ params: query, method: "GET" });
  }
}
const buildHook = (config) => {
  let canceler;
  const cancelAjax = (next, config2) => {
    var _a;
    canceler = (_a = config2.meta) == null ? void 0 : _a.canceler;
    return next();
  };
  onUnmounted(() => {
    if (canceler)
      canceler();
  });
  onDeactivated(() => {
    if (canceler)
      canceler();
  });
  const agent = new AjaxHookInstance(getNiceAxiosInstance().attach([cancelAjax]), config);
  return (data, options) => {
    return agent.exec({ data, ...options });
  };
};

export { AjaxContainer, AjaxHookInstance, AjaxMethods, ContentTypeEnum, addCancelerPlugin, ajax, buildAfterPlugin, buildBeforePlugin, buildHook, cancelAllAjax, createNiceAxios, errorResult, errorResultNull, fetch, getNiceAxiosInstance, getNiceAxiosOptions, removeCancelerPlugin };
